import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "@environments/environment";

@Component({
  selector: "app-college-course-details-create",
  templateUrl: "./college-course-details-create.component.html",
  styleUrls: ["./college-course-details-create.component.scss"],
})
export class CollegeCourseDetailsCreateComponent implements OnInit {
  public tinyConfig = {
    base_url: '/assets/tinymce',
    suffix: '.min',
    height: 400,
    menubar: true,
    plugins: 'code table link image lists',
    apiKey: '',
    toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | table | link image | code',
  };
  @Input() record: any;          // ✅ data for edit
  @Input() collegeId: string;    // ✅ college id from parent
  @Output() formSaved = new EventEmitter<void>();  // ✅ notify parent
  @Output() cancel = new EventEmitter<void>();     // ✅ cancel event

  public Form: FormGroup;
  submitted = false;
  message = null;
  class = null;
  action = null;
  isShown: boolean = false;
  courseduration = null;
  degree = null;
  course = null;
  streams = null;
  branch = null;
  college = null;
  programtype = null;
  onlineexam = null;
  DeliveryMode = null;
  intakeseason = null;
  currentYear: number;

  constructor(
    private ActivatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private router: Router
  ) {
    this.currentYear = new Date().getFullYear();
  }

  ngOnInit() {
    this.action = "Submit";

    this.Form = this.formBuilder.group({
      fees: ["", Validators.required],
      course: ["", Validators.required],
      college: ["", Validators.required],
      degree: ["", Validators.required],
      description: ["", Validators.required],
      courseduration: ["", Validators.required],
      streams: ["", Validators.required],
      programtype: ["", Validators.required],
      onlineexam: this.formBuilder.array([], Validators.required),
      branch: ["", Validators.required],
      deliverymode: ["", Validators.required],
      intakeyear: ["", Validators.required],
      eligibility: ["", Validators.required],
      intakeseason: ["", Validators.required],
      _id: [""],
    });
    if (this.record) {
      this.action = "Update";
      this.Form.patchValue(this.record);
    } else {
      this.action = "Submit";
      this.Form.patchValue({ college: this.collegeId });
    }

    this.loadDropdowns();
    this.loadDegreeAndCollege(); // Separate API calls for degree and college
    this.loadCourseDetails();
  }

  get f() {
    return this.Form.controls;
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Userinfo: JSON.stringify({
        _id: localStorage.getItem("adminSess"),
        role: localStorage.getItem("userRole"),
      }),
    });
  }

  private checkLogout(data: any) {
    if (data.message == "Logged Out!") {
      this.router.navigateByUrl("/auth/login");
    }
  }

  loadDropdowns() {
    const params = {
      country: "true",
      city: "true",
      state: "true",
      streams: "true",
      branch: "true",
      course: "true",
      typeofcollege: "true",
      programtype: "true",
      entranceexam: "true",
      fees: "true",
      intakeSession: "true",
      deliveryMode: "true",
      courseduration: "true",
      genderaccepted: "true",
      ranking: "true",
    };

    const headers = this.getHeaders();

    this.http
      .get<any>(`${environment.apiUrl}/helper/getAllDetails`, { params, headers })
      .subscribe(
        (data) => {
          this.checkLogout(data);
          this.streams = data.streams;
          this.branch = data.branch;
          this.course = data.course;
          this.onlineexam = data.entranceexam;
          this.courseduration = data.courseduration;
          this.DeliveryMode = data.deliveryMode;
          this.intakeseason = data.intakeSession;
          this.programtype = data.programtype;
        },
        (err: HttpErrorResponse) => {
          console.error("Error loading dropdowns:", err);
        }
      );
  }

  loadDegreeAndCollege() {
    const headers = this.getHeaders();

    // Load Degree Options
    this.http
      .get<any>(`${environment.apiUrl}/degree/`, { headers })
      .subscribe(
        (data) => {
          this.checkLogout(data);
          this.degree = data.results;
        },
        (err: HttpErrorResponse) => {
          console.error("Error loading degrees:", err);
        }
      );

    // Load College Options
    this.http
      .get<any>(`${environment.apiUrl}/college/`, { headers })
      .subscribe(
        (data) => {
          this.checkLogout(data);
          this.college = data.results;
        },
        (err: HttpErrorResponse) => {
          console.error("Error loading colleges:", err);
        }
      );
  }

  get onlineexamArray(): FormArray {
    return this.Form.get('onlineexam') as FormArray;
  }

  onCheckboxChange(event: any) {
    const onlineexam = this.onlineexamArray;
    if (event.target.checked) {
      onlineexam.push(new FormControl(event.target.value));
    } else {
      const index = onlineexam.controls.findIndex(x => x.value === event.target.value);
      if (index !== -1) {
        onlineexam.removeAt(index);
      }
    }
  }

  isExamSelected(examId: string): boolean {
    return this.onlineexamArray.controls.some(x => x.value === examId);
  }

  loadCourseDetails() {
    this.ActivatedRoute.params.subscribe((params) => {
      let id = params["id"];
      const headers = this.getHeaders();

      this.http
        .get<any>(`${environment.apiUrl}/collegecoursedetails/${id}`, { headers })
        .subscribe(
          (data) => {
            this.checkLogout(data);
            if (data) {
              this.action = "Update";
              this.Form.patchValue({
                _id: data._id,
                fees: data.fees,
                streams: data.streams,
                college: data.college,
                description: data.description,
                courseduration: data.courseduration,
                degree: data.degree,
                course: data.course,
                branch: data.branch,
                programtype: data.programtype,
                onlineexam: data.onlineexam,
                intakeyear: data.intakeyear,
                deliverymode: data.deliverymode,
                intakeseason: data.intakesession
              });
            }
          },
          (err: HttpErrorResponse) => {
            console.error("Error loading course details:", err);
          }
        );
    });
  }

  onFormSubmit() {
    this.submitted = true;
    console.log("Form submitted!");

    /*   if (this.Form.invalid) {
        console.log("Form is invalid");
        return;
      } */

    const data = {
      fees: this.Form.value.fees,
      college: this.Form.value.college,
      course: this.Form.value.course,
      description: this.Form.value.description,
      courseduration: this.Form.value.courseduration,
      degree: this.Form.value.degree,
      streams: this.Form.value.streams,
      branch: this.Form.value.branch,
      onlineexam: this.Form.value.onlineexam,
      programtype: this.Form.value.programtype,
      intakeyear: this.Form.value.intakeyear,
      deliverymode: this.Form.value.deliverymode,
      intakesession: this.Form.value.intakeseason
    };

    const headers = this.getHeaders();

    if (this.action === "Submit") {
      this.http.post<any>(`${environment.apiUrl}/collegecoursedetails/`, data, { headers })
        .subscribe(
          (res) => {
            this.checkLogout(res);
            this.message = "Successfully Added";
            this.class = "btn-success rounded px-1 py-1";
            this.router.navigateByUrl("/college-details/college-course-details-list");
            this.formSaved.emit();   // ✅ emit on success
          },
          (err: HttpErrorResponse) => {
            console.error("Error adding course detail:", err);
          }
        );
    } else {
      const id = this.Form.value._id;
      this.http.patch<any>(`${environment.apiUrl}/collegecoursedetails/${id}`, data, { headers })
        .subscribe(
          (res) => {
            this.checkLogout(res);
            this.message = "Successfully Updated";
            this.class = "btn-success rounded px-1 py-1";
            this.router.navigateByUrl("/college-details/college-course-details-list");
            this.formSaved.emit();   // ✅ emit on success
          },
          (err: HttpErrorResponse) => {
            console.error("Error updating course detail:", err);
          }
        );
    }
  }
  onCancel() {
    if (this.cancel.observers.length > 0) {
      // 🔹 Case 1: Opened inside modal (popup)
      this.cancel.emit();
    } else {
      // 🔹 Case 2: Opened as a standalone routed page
      this.router.navigateByUrl("/college-details/college-course-details-list");
    }
  }


}
